/**
 * Location Grouping Utilities
 * Hierarchical filtering: Region → Country → City
 */

// Regional groupings
export const REGIONS = {
  'North America': ['United States', 'Canada', 'Mexico'],
  'Europe': [
    'United Kingdom', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands',
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Poland', 'Switzerland',
    'Austria', 'Belgium', 'Ireland', 'Portugal', 'Czech Republic', 'Romania',
    'Greece', 'Hungary', 'Ukraine', 'Bulgaria', 'Serbia', 'Croatia', 'Slovenia'
  ],
  'Asia-Pacific': [
    'China', 'Japan', 'India', 'South Korea', 'Singapore', 'Australia',
    'Hong Kong', 'Taiwan', 'Thailand', 'Indonesia', 'Malaysia', 'Philippines',
    'Vietnam', 'New Zealand', 'Pakistan', 'Bangladesh'
  ],
  'Middle East': [
    'United Arab Emirates', 'Israel', 'Saudi Arabia', 'Turkey', 'Qatar',
    'Kuwait', 'Bahrain', 'Oman', 'Jordan', 'Lebanon', 'Egypt'
  ],
  'Latin America': [
    'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Venezuela',
    'Uruguay', 'Ecuador', 'Bolivia', 'Paraguay'
  ],
  'Africa': [
    'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco', 'Ghana',
    'Ethiopia', 'Tanzania', 'Uganda', 'Rwanda'
  ]
};

// US States to full names (for better grouping)
export const US_STATES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
};

/**
 * Parse location string to extract country/region
 * @param {String} location - "City, Country" or "City, State"
 * @returns {Object} { city, region, country }
 */
export const parseLocation = (location) => {
  if (!location) return { city: null, region: null, country: null };

  const parts = location.split(',').map(p => p.trim());

  if (parts.length === 1) {
    return { city: parts[0], region: null, country: null };
  }

  const city = parts[0];
  const lastPart = parts[parts.length - 1];

  // Check if it's a US state
  if (US_STATES[lastPart]) {
    return {
      city,
      region: lastPart, // State abbreviation
      country: 'United States'
    };
  }

  // Otherwise it's a country
  return {
    city,
    region: null,
    country: lastPart
  };
};

/**
 * Get region for a country
 * @param {String} country - Country name
 * @returns {String} Region name or 'Other'
 */
export const getRegionForCountry = (country) => {
  for (const [region, countries] of Object.entries(REGIONS)) {
    if (countries.includes(country)) {
      return region;
    }
  }
  return 'Other';
};

/**
 * Group locations hierarchically
 * @param {Array} locations - Array of location strings
 * @returns {Object} Hierarchical structure { region: { country: [cities] } }
 */
export const groupLocationsHierarchically = (locations) => {
  if (!Array.isArray(locations)) {
    return {};
  }

  const hierarchy = {};

  locations.forEach(location => {
    if (!location || typeof location !== 'string') return;
    const { city, region, country } = parseLocation(location);

    if (!country) return; // Skip if can't parse

    const regionName = getRegionForCountry(country);

    if (!hierarchy[regionName]) {
      hierarchy[regionName] = {};
    }

    if (!hierarchy[regionName][country]) {
      hierarchy[regionName][country] = [];
    }

    // Add city if not already present
    if (city && !hierarchy[regionName][country].includes(location)) {
      hierarchy[regionName][country].push(location);
    }
  });

  return hierarchy;
};

/**
 * Check if location matches filter criteria
 * @param {String} location - Location string to test
 * @param {Array} selectedCountries - Array of selected countries
 * @param {Array} selectedRegions - Array of selected regions
 * @returns {Boolean} true if location matches
 */
export const locationMatchesFilter = (location, selectedCountries = [], selectedRegions = []) => {
  if (selectedCountries.length === 0 && selectedRegions.length === 0) {
    return true; // No filters applied
  }

  const { country } = parseLocation(location);
  if (!country) return false;

  const region = getRegionForCountry(country);

  // Check if country is directly selected
  if (selectedCountries.includes(country)) {
    return true;
  }

  // Check if region is selected (includes all countries in that region)
  if (selectedRegions.includes(region)) {
    return true;
  }

  return false;
};

/**
 * Get flat list of countries from locations
 * @param {Array} locations - Array of location strings
 * @returns {Array} Sorted unique countries
 */
export const getUniqueCountries = (locations) => {
  const countries = new Set();

  locations.forEach(location => {
    const { country } = parseLocation(location);
    if (country) {
      countries.add(country);
    }
  });

  return Array.from(countries).sort();
};
